import DocumentSource from './DocumentSource';
import Notification from '../core/Notification';
import { Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import PropTypes from 'prop-types';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import ComplexExpressionButton from '../core/ComplexExpressionButton';
import RestrictionButton from '../core/RestrctionButton';
import SecondaryButton from '../core/SecondaryButton';
import { globalStyles as styles } from '../core/style/gstheme';
import { BN_NAMESPACE } from '../core/utils/constants';
import withGS from '../core/withGShoc';
import GS from '../grafsync/grafsync';
import BlankNodeHandler from './BlankNodeHandler';
import FindResourceDialog from './FindResourceDialog';
import { deleteBlankNode } from '../core/utils/utils';
import AdvancedButtons from './AdvancedButtons';

class AnonymousClassHandler extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      isErrored: undefined,
      constraintType: undefined
    };
    this.cancellable = null;
  }

  // If a property is added that conflicts with any constraint we need to kill dialogs
  static getDerivedStateFromProps(props, state) {
    const dataProps = Object.keys(props.data).filter(k => ["intersectionOf", "unionOf", "complementOf", "oneOf"].includes(k) && props.data[k] !== undefined);
    if (state.constraintType && dataProps.length > 0) {
      return {
        constraintType: undefined
      };
    }
    return null;
  }

  setConstraintType = (constraintType) => this.setState({ constraintType });

  exitFindResource = () => this.setState({ constraintType: undefined });

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  addNewResource = (iri) => {
    const property = this.state.constraintType ?
      this.state.constraintType
      :
      "unionOf" in this.props.data ?
        "unionOf"
        :
        "intersectionOf" in this.props.data ?
          "intersectionOf"
          :
          "oneOf";
    GS.insert(this.props.id, this.props.graph, property, iri);
    if (this.state.constraintType) this.setConstraintType(undefined);
  };

  addNewRestriction = (forceProp) => async () => {
    const { graph, id, data } = this.props;
    const restrictionIRI = BN_NAMESPACE + uuidv4();
    let newRestriction = {
      id: restrictionIRI,
      type: "Restriction",
      invisibility: this.props.sourceComponent
    };
    await GS.insertResource(newRestriction, restrictionIRI, this.props.graph, [id]);
    const property = forceProp ?
      forceProp
      :
      "unionOf" in data ?
        "unionOf"
        :
        "intersectionOf" in data ?
          "intersectionOf"
          :
          "oneOf";
    GS.insert(id, graph, property, restrictionIRI);
    if (this.state.constraintType) this.setConstraintType(undefined);
  };

  addComplementRestriction = async () => {
    const { graph, id } = this.props;
    const restrictionIRI = BN_NAMESPACE + uuidv4();
    let newRestriction = {
      id: restrictionIRI,
      type: "Restriction",
      invisibility: this.props.sourceComponent
    };
    await GS.insertResource(newRestriction, restrictionIRI, this.props.graph, [id]);
    GS.addProperty(id, graph, "complementOf", restrictionIRI, null, null, graph);
    this.setConstraintType(undefined);
  };

  addComplementClass = (iri) => {
    GS.addProperty(this.props.id, this.props.graph, "complementOf", iri, null, null, this.props.graph);
    this.setConstraintType(undefined);
  };

  addClass = async (property) => {
    const { graph } = this.props;
    const classIRI = BN_NAMESPACE + uuidv4();
    let newClass = {
      id: classIRI,
      invisibility: this.props.sourceComponent,
      type: "OwlClass"
    };
    // We need to make sure the subject is aware of this as it will be loaded immediately (so before hits DB maybe)
    await GS.insertResource(newClass, classIRI, graph, [this.props.id]);
    GS.addProperty(this.props.id, graph, property, classIRI);
  };

  addClassToList = (forceProp) => async () => {
    const { graph, id, data } = this.props;
    const classIRI = BN_NAMESPACE + uuidv4();
    let newClass = {
      id: classIRI,
      invisibility: this.props.sourceComponent,
      type: "OwlClass"
    };
    // We need to make sure the subject is aware of this as it will be loaded immediately (so before hits DB maybe)
    await GS.insertResource(newClass, classIRI, graph, [id]);
    const property = forceProp ?
      forceProp
      :
      "unionOf" in data ?
        "unionOf"
        :
        "intersectionOf" in data ?
          "intersectionOf"
          :
          "oneOf";
    GS.insert(id, graph, property, classIRI);
  };

  deleteListItem = (listProp, position, iri) => GS.remove(iri, this.props.graph, listProp, position);

  deleteResource = () => deleteBlankNode(this.props.id, this.props.graph);

  render() {

    const { data, id, graph, components, viewLang, defaultLang, projectIRI, sourceResource, top, useWorkflow,
      context, sourceComponent, classes } = this.props;
    const { constraintType } = this.state;
    const isReadOnly = this.props.isReadOnly || data.pendingDeletion;

    // Extreme edge case that this will happen. Wil require concurrent adding at the same time
    const conflictingProps = Object.keys(data).filter(k => ["intersectionOf", "unionOf", "complementOf", "oneOf"].includes(k) && data[k] !== undefined);
    if (conflictingProps.length > 1) {
      return (
        <div data-test="ce-outer" className={classes.incompleteData}>
          <div>
            {!isReadOnly &&
              <div style={{ flex: 1, textAlign: 'right' }}>
                <Tooltip title='Delete expression'>
                  <span style={{ float: 'right', marginBottom: 6, marginLeft: 6 }}>
                    <Typography variant="body2">
                      <IconButton data-test="delete-expression" size="small" onClick={this.deleteResource} className={classes.deleteButton}>
                        <DeleteIcon />
                      </IconButton>
                    </Typography>
                  </span>
                </Tooltip>
              </div>
            }
            <div style={{ clear: 'both' }}>
              {data.pendingDeletion ?
                <Notification error={true} text="This class is pending deletion." dataTest="pending-deletion" />
                :
                <Notification text={"This anonymous class contains conflicting properties: " + conflictingProps.join(", ") + ". Please delete."} dataTest="conflicting" />
              }
            </div>
          </div>
        </div>
      );
    }

    let label = undefined;
    let propName = undefined;

    if ("intersectionOf" in data) {
      label = "Intersection Of";
      propName = "intersectionOf";
    }
    else if ("unionOf" in data) {
      label = "Union Of";
      propName = "unionOf";
    }
    else if ("complementOf" in data) {
      label = "Complement Of";
      propName = "complementOf";
    }
    else if ("oneOf" in data) {
      label = "One Of";
      propName = "oneOf";
    }

    const items = data[propName];
    let excludeList = [sourceResource];
    if (items) {
      Array.isArray(items) ?
        excludeList = excludeList.concat(items)
        :
        excludeList.push(items);
    }

    if (constraintType) {
      return constraintType === "oneOf" ?
        <FindResourceDialog
          key={viewLang}
          useButton={true}
          permitExternal={false}
          open={true}
          useWorkflow={useWorkflow}
          components={components}
          exclude={excludeList}
          defaultLang={defaultLang}
          sourceComponent={sourceComponent}
          searchClass={[
            "http://www.w3.org/2002/07/owl#NamedIndividual"
          ]}
          title="Add Named Individual"
          project={projectIRI}
          graph={graph}
          viewLang={viewLang}
          onExitFindResource={this.exitFindResource}
          onAddResource={this.addNewResource} />
        :
        <FindResourceDialog
          key={viewLang}
          useButton={true}
          permitExternal={true}
          open={true}
          useWorkflow={useWorkflow}
          components={components}
          exclude={excludeList}
          defaultLang={defaultLang}
          permittedExternalType="OwlClass"
          sourceComponent={sourceComponent}
          searchClass={[
            "http://www.w3.org/2002/07/owl#Class"
          ]}
          title="Add Class"
          project={projectIRI}
          graph={graph}
          viewLang={viewLang}
          onExitFindResource={this.exitFindResource}
          onAddResource={constraintType === "complementOf" ?
            this.addComplementClass
            :
            this.addNewResource} />;
    }

    if (!items) return <div
      data-test="ce-outer"
      className={classes.incompleteData}
    >
      {data.pendingDeletion &&
        <Notification error={true} text="This class is pending deletion." dataTest="pending-deletion" />
      }
      {isReadOnly && !data.pendingDeletion &&
        <Notification text="This contains partially completed structures." dataTest="empty-partial" />
      }
      {!isReadOnly &&
        <div data-test="ce-selector" style={{ marginBottom: 12 }}>
          <Tooltip title='Delete expression'>
            <span style={{ float: 'right', marginBottom: 6 }}>
              <Typography variant="body2">
                <IconButton data-test="delete-expression" size="small" onClick={this.deleteResource} className={classes.deleteButton}>
                  <DeleteIcon />
                </IconButton>
              </Typography>
            </span>
          </Tooltip>
          <Typography variant="caption" className={classes.instruction}>Choose from</Typography>
          <span className={classes.optionsButton}>
            <AdvancedButtons
              property="intersectionOf"
              title="Intersection"
              dataTest="intersection-of"
              onAddExpression={this.addClassToList}
              onAddRestriction={this.addNewRestriction}
              onAddSimple={(property) => () => this.setConstraintType(property)}
            />
          </span>
          <span className={classes.optionsButton}>
            <AdvancedButtons
              property="unionOf"
              dataTest="union-of"
              title="Union"
              onAddExpression={this.addClassToList}
              onAddRestriction={this.addNewRestriction}
              onAddSimple={(property) => () => this.setConstraintType(property)}
            />
          </span>
          <span className={classes.optionsButton}>
            <AdvancedButtons
              property="complementOf"
              title="Complement"
              dataTest="complement-of"
              onAddExpression={(property) => () => this.addClass(property)}
              onAddRestriction={() => this.addComplementRestriction}
              onAddSimple={(property) => () => this.setConstraintType(property)}
            />
          </span>
          <span className={classes.optionsButton}>
            <SecondaryButton dataTest="one-of" onClick={() => this.setConstraintType("oneOf")}>One Of</SecondaryButton>
          </span>
        </div>
      }
    </div>;

    const itemsList = Array.isArray(items) ? items : [items];
    const isComplete = items.length > 0;

    if (data.pendingDeletion) return null;

    return (
      <div
        data-test="ce-outer"
        className={isComplete ?
          top ?
            classes.completeDataDarkTop
            :
            classes.completeDataDark
          :
          classes.incompleteData}
      >
        {data.pendingDeletion &&
          <Notification error={true} text="This class is pending deletion." dataTest="pending-deletion" />
        }
        {isReadOnly && !isComplete && !data.pendingDeletion &&
          <Notification text="This contains partially completed structures." dataTest="partial" />
        }
        <div>
          <div style={{ display: 'flex', width: '100%' }}>
            <div>
              <Typography variant="caption" style={{ color: 'black' }} data-test="constraint-label">{label}</Typography>
            </div>
            {!isReadOnly &&
              <div style={{ flex: 1, textAlign: 'right' }}>
                <Tooltip title='Delete expression'>
                  <span style={{ float: 'right', marginBottom: 6, marginLeft: 6 }}>
                    <Typography variant="body2">
                      <IconButton data-test="delete-expression" size="small" onClick={this.deleteResource} className={classes.deleteButton}>
                        <DeleteIcon />
                      </IconButton>
                    </Typography>
                  </span>
                </Tooltip>
                {propName !== "oneOf" && propName !== "complementOf" &&
                  <React.Fragment>
                    <span style={{ marginLeft: 6 }}>
                      <RestrictionButton dataTest="add-restriction" onClick={this.addNewRestriction()} title="Add a restriction" />
                    </span>
                    <span style={{ marginLeft: 6 }}>
                      <ComplexExpressionButton dataTest="add-complex-class" onClick={this.addClassToList()} title="Add a complex class" />
                    </span>
                  </React.Fragment>
                }
                {(propName !== "complementOf" || items.length === 0) &&
                  <span style={{ marginLeft: 6 }}>
                    {propName === "oneOf" ?
                      <FindResourceDialog
                        key={viewLang}
                        source={sourceResource}
                        permitExternal={false}
                        components={components}
                        useWorkflow={useWorkflow}
                        exclude={excludeList}
                        defaultLang={defaultLang}
                        sourceComponent={sourceComponent}
                        buttonTitle="Add a named individual"
                        title="Add Named Individual"
                        searchClass="http://www.w3.org/2002/07/owl#NamedIndividual"
                        project={projectIRI}
                        graph={graph}
                        viewLang={viewLang}
                        onAddResource={this.addNewResource} />
                      :
                      <FindResourceDialog
                        key={viewLang}
                        source={sourceResource}
                        permitExternal={true}
                        useWorkflow={useWorkflow}
                        permittedExternalType="OwlClass"
                        components={components}
                        exclude={excludeList}
                        defaultLang={defaultLang}
                        sourceComponent={sourceComponent}
                        buttonTitle="Add a class"
                        title="Add Class"
                        searchClass="http://www.w3.org/2002/07/owl#Class"
                        project={projectIRI}
                        graph={graph}
                        viewLang={viewLang}
                        onAddResource={this.addNewResource} />
                    }
                  </span>
                }
              </div>
            }
          </div>
          {(propName === "unionOf" || propName === "intersectionOf") && itemsList.length === 1 &&
            <div style={{ marginBottom: 12 }}>
              <Notification text="For this to be needed there should be at least two items." dataTest="at-least-two" />
            </div>
          }
          {top &&
            <div style={{ width: 'calc(100% - 60px)', marginBottom: 12 }}>
              <DocumentSource
                graph={graph}
                componentIRI={sourceComponent}
                viewLang={viewLang}
                defaultLang={defaultLang}
                sources={data.documentSource} />
            </div>
          }
          <div data-test="ce-content">
            {itemsList.map((item, itemIndex) => item.startsWith(BN_NAMESPACE) ?
              <div
                data-test="blanknode-container"
                key={item}
                style={{ marginBottom: itemIndex < itemsList.length - 1 ? 12 : 0 }}>
                <BlankNodeHandler
                  key={item}
                  id={item}
                  useWorkflow={useWorkflow}
                  handleNotAvailable={true}
                  context={context}
                  parent={data}
                  sourceResource={sourceResource}
                  sourceComponent={sourceComponent}
                  graph={graph}
                  projectIRI={projectIRI}
                  components={components}
                  isReadOnly={isReadOnly}
                  viewLang={viewLang}
                  defaultLang={defaultLang} />
              </div>
              :
              <CommonIriDisplay
                id={item}
                key={item}
                onDeleteIRI={() => this.deleteListItem(propName, itemIndex, id)}
                components={components}
                context={context}
                contextProperty="isDefinedBy"
                projectIRI={projectIRI}
                handleNotAvailable={true}
                property={propName}
                isReadOnly={propName === "complementOf" || isReadOnly}
                compact={true}
                graph={graph}
                source={id}
                wrap={true}
                viewLang={viewLang}
                defaultLang={defaultLang} />
            )}
          </div>
        </div>
      </div>
    );
  }
}

AnonymousClassHandler.propTypes = {
  id: PropTypes.string,
  data: PropTypes.object,
  defaultLang: PropTypes.string,
  viewLang: PropTypes.string,
  top: PropTypes.bool,
  sourceResource: PropTypes.string.isRequired,
  sourceComponent: PropTypes.string.isRequired,
  components: PropTypes.array,
  context: PropTypes.string,
  graph: PropTypes.string,
  projectIRI: PropTypes.string.isRequired,
  useWorkflow: PropTypes.bool,
  isReadOnly: PropTypes.bool
};

export default withStyles(styles)(withGS(AnonymousClassHandler));